import { Component, ViewChild, ElementRef, TemplateRef, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { BbSelectOption } from '../../shared/bb-select/bb-select.component';
import { SapiEntity } from '../../../services/sapi.service';
import { LocationService } from '../../../services/location.service';
import { Observable, Subscription } from 'rxjs';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { BbTableService, BbTableSelectOption, TableRow } from '../../../services/bb-table.service';
import { TabItem } from '../../shared/bb-page-header/bb-page-header.component';
import { DialogDefinition } from '../../../services/overlay.service';

declare let window: any;

export interface Social extends SapiEntity {
	privacy: 'public' | 'unlisted' | 'private';
	name: string;
	exportsystemname: 'YouTube';
	exportsystemid: number;
	status: 'active' | 'inactive';
	filterType: 'filters' | 'solr' | 'cliplist' | string;
	automatedSync: boolean;
	authExpired: boolean;
	authtokenid: string;
	cliplistid: string;
	cliplist: {
		usetype: 'social';
		limit?: number;
		sort?: string;
		filterType?: string;
		query?: string;
		filters?: any;
	};
}

@Component({
	selector: 'social-details',
	templateUrl: './social-details.component.html',
	styleUrls: ['./social-details.component.scss']
})

export class SocialDetailsComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {
	@ViewChild('formRef', { static: true }) form: NgForm;
	@ViewChild('authorizeTemplate') authorizeTemplate: TemplateRef<any>;
	@ViewChild('authenticationButton') authenticationButton: ElementRef;

	filterOption: string;
	filterOptions: BbSelectOption[] = [];

	isDisabled: boolean;
	queryStringChange = false;

	exportchannelUrl: string;
	authenticationUrl: SafeResourceUrl;

	authorizeWindow: Window;

	checkForWindowTimer: Observable<number>;

	rows: TableRow[] = null;
	rowsTotal: number;

	subscriptions: Subscription;

	activeLabel: string;
	inactiveLabel: string;

	tabItems: TabItem[];

	entity: Social;

	// TODO: Implement rowsContainLiveClips or remove from html
	rowsContainLiveClips: boolean;

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		protected translate: TranslateService,
		protected sanitizer: DomSanitizer,
		protected bbTableService: BbTableService,
		private changeDetector: ChangeDetectorRef,
		protected locationService: LocationService) {
		super(route, location);

		this.entityConfiguration = {
			name: 'exportchannel',
			parentPath: '/library/social',
			basePath: '/library/social/',
			entityTranslation: 'social'
		};

		this.activeLabel = this.translate.instant('ACTIVE');
		this.inactiveLabel = this.translate.instant('INACTIVE');



		window.socialCallback = (status: string, token_id: string) => {
			if (status === 'ok') {
				this.entity.authtokenid = token_id;
				this.entity.authExpired = false;
				this.overlayService.createToast(this.translate.instant('SOCIAL_AUTHENTICATION_SUCCESSFUL'));
				this.onAuthUpdated();
			} else {
				this.overlayService.createToast(this.translate.instant('SOCIAL_AUTHENTICATION_ERROR'), 'times-circle');
			}
		};

		this.filterOptions = [];
		['ALL', 'NEW', 'QUEUED', 'FAILED', 'EXPORTED'].forEach((filter: string) => {
			const option: BbSelectOption = { label: this.translate.instant(filter), value: filter.toLowerCase() };
			if (filter === 'ALL') {
				option.default = true;
			}
			this.filterOptions.push(option);
		});

		this.filterOption = 'all';

		this.subscriptions = new Subscription();
		this.subscriptions.add(
			this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => {
				this.rows = rows;
			})
		);

		this.subscriptions.add(
			this.bbTableService.totalChangedObservable.subscribe((total: number) => {
				this.rowsTotal = total;
			})
		);

		this.tabItems = [
			{
				title: this.translate.instant('GENERAL'),
				routerLink: ['/library/social', 'general'],
				icon: 'tags'
			},
			{
				title: this.translate.instant('AUTOMATED_SYNCING'),
				routerLink: ['/library/social', 'automated-synching'],
				icon: 'magic'
			}
		];

		this.bbTableService
			.setEntity('commonclip')
			.sort('publisheddate', 'desc')
			.setLimit(15);
	}

	onAuthUpdated() {
		this.sapiService.get(this.entityConfiguration.name, this.entity.id).subscribe((updatedEntity) => {
			'remoteId remotePageTitle remotePageDescription remoteUrl remoteLogoImageUrl remotePageId remoteBannerImageUrl name'.split(' ').forEach((prop) => {
				this.entity[prop] = updatedEntity[prop];
			});
		});
	}

	ngOnInit() {
		super.ngOnInit();

		this.exportchannelUrl = this.locationService.server + '/sapi/exportchannel/';

		if (this.isNew) {
			this.entity = {
				privacy: 'public',
				name: '',
				exportsystemname: 'YouTube',
				exportsystemid: 4,
				status: 'inactive',
				filterType: 'filters',
				cliplist: {
					usetype: 'social',
					limit: 30,
					sort: 'publisheddate desc',
					filterType: 'filters',
					filters: []
				}
			} as Social;
		}

		this.subscriptions.add(
			this.entityLoaded.subscribe(() => {
				this.showExportedItems();
			})
		);

		this.subscriptions.add(
			this.route.params.subscribe((params: Params) => {
				if (params && params.tab === 'general') {
					if (this.entity) {
						this.showExportedItems();
					}
				}
			})
		);

		this.queryChangedCheck();

		this.subscriptions.add(
			this.bbTableService.queryObservable.subscribe((query: string) => {
				if (this.activeTab === 'automated-synching' && !this.queryStringChange) {
					if (query) {
						query = query
							.replace('(typeSort:"MediaClip") AND (mediatypeSort:"video") AND (sourcetypeSort:"on_demand") AND (status:"published")', '')
							.trim()
							.replace(/ AND$/, '');
					} else {
						query = '';
					}
					if (query.trim() === '') {
						query = '*:*';
					}

					if (this.entity && this.entity.cliplist) {
						this.entity.cliplist.query = query;
					}
				}
			})
		);
	}

	showExportedItems() {
		if (this.activeTab === 'general' && this.entity.id !== 'new') {
			const baseFilterSet = [
				{
					filters: [{
						type: 'mediaclip',
						field: 'exportchannelid',
						operator: 'is',
						value: this.entity.id
					}]
				}
			];

			this.bbTableService
				.setSelect(BbTableSelectOption.none)
				.setConfigurable(false)
				.sort('id', 'desc')
				.setBaseFilterSet(baseFilterSet)
				.getRows(true);
		}
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	queryChangedCheck(queryChange?: boolean) {
		if (queryChange) {
			this.queryStringChange = queryChange;
		} else {
			this.queryStringChange = false;
		}
	}

	filterChanged(filter: any) {
		let query = '';
		switch (filter) {
			case 'all':
				query = '';
				break;
			case 'new':
				query = 'YouTube_status:""';
				break;
			case 'queued':
				query = 'YouTube_status:new';
				break;
			case 'failed':
				query = 'YouTube_status:error';
				break;
			default:
				query = 'YouTube_status:' + filter;
				break;
		}

		this.bbTableService
			.setFilterQuery(query)
			.getRows(true);
	}

	rowsChanged(rows: TableRow[]) {
		this.rows = rows;
		this.rowsTotal = this.rows && this.rows.length || 0;
		this.changeDetector.detectChanges();
	}

	save(force?: boolean, extraHeaders?: any): Observable<void> {
		const save = super.save(force, extraHeaders, false);
		if (this.entity) {
			if (this.entity.filterType === 'cliplist') {
				this.entity.cliplist = null;
			} else {
				delete this.entity.cliplistid;
			}
		}
		save.subscribe();
		return save;
	}


	deleteFromYoutube(row: TableRow) {
		const config = { entity: this.translate.instant('MEDIACLIP').toLowerCase(), id: row.id };

		const dialog: DialogDefinition = {
			icon: 'trash-o',
			title: this.translate.instant('DELETE_MEDIACLIP_YOUTUBE', config),
			contents: this.translate.instant('DELETE_MEDIACLIP_YOUTUBE_CONFIRM'),
			onConfirm: () => {
				this.sapiService.delete('exportchannel', this.entity.id, 'mediaclip/' + row.id).subscribe(() => {
					this.overlayService.createToast(this.translate.instant('DELETE_MEDIACLIP_YOUTUBE_DELETED', config));
				});
			}
		};

		this.overlayService.createDialog(dialog);
	}

	openClip(row: TableRow) {
		if (row.data.YouTube_id) {
			window.open('https://www.youtube.com/watch?v=' + row.data.YouTube_id);
		}
	}

	trackBy(index: number, item: any) {
		if (item.id !== 'new') {
			return item.id || index;
		}
		return index;
	}
}
